import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import type { FuseSearchOptions, IFuseOptions } from "fuse.js";
import { cloneDeep, isEqual } from "lodash-es";
import {
  JSX,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import api, { queries } from "@oneleet/api_client";
import type { User } from "@oneleet/api_client";

import { clearUser, identifyUser } from "../sentry";

export const DEFAULT_LOADING_DELAY = 200;

/**
 * Shows a loading indicator after a delay.
 * @param isLoading if true, will show the loading indicator after a delay
 * @param delay Delay in milliseconds to wait before showing the loading indicator
 */
export const useDelayedLoading = ({
  isLoading,
  delay = DEFAULT_LOADING_DELAY,
}: {
  isLoading: boolean;
  delay?: number;
}) => {
  const [showLoading, setShowLoading] = useState(delay === 0);

  useEffect(() => {
    let timeoutId: number;
    if (isLoading) {
      timeoutId = window.setTimeout(() => setShowLoading(true), delay);
    } else {
      setShowLoading(false);
    }

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, delay]);

  return showLoading;
};

const TWO_SECONDS = 2000;

export const useJustCopied = ({
  delay = TWO_SECONDS,
}: { delay?: number } = {}) => {
  const [justCopied, setJustCopied] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (justCopied) {
      timeout = setTimeout(() => {
        setJustCopied(false);
      }, delay);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [justCopied, delay]);

  return { justCopied, setJustCopied };
};

// useIsModified returns true if the value has been modified from it's first non-undefined
// value, false otherwise.
// note that we create deep clones of the values in case array refs are passed, in which case
// comparison would always return true
export function useIsModified<T>(originalValue?: T, isNullable = false) {
  const [value, setValue] = useState<T | undefined>(originalValue);

  const ref = useRef<T>(null);
  let isModified;

  if (!isNullable && !originalValue && !value) {
    isModified = false;
  } else {
    if (!ref.current) {
      if (originalValue || isNullable) {
        ref.current = cloneDeep(originalValue) ?? null;
      } else {
        ref.current = cloneDeep(value) ?? null;
      }
    }

    isModified = !isEqual(ref.current, value);
  }

  return [value, setValue, isModified] as const;
}

export function usePrevious<T>(value: T): T | null {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

/**
 * Periodically checks for whether the user is currently logged in/their email is verified and redirects
 * the user accordingly.
 *
 * Some examples:
 *
 * For login/register, set checkAuthenticated = false, checkEmailVerified = false, which will redirect to the index if the user is logged in.
 * For authenticated routes, set checkAuthenticated = true, checkEmailVerified = true, which will redirect if the user is not logged in or their email is not verified.
 * For /verify-email, set checkAuthenticated = true, checkEmailVerified = false, which will redirect to the index if the user is logged in but their email is not verified.
 *
 * @deprecated use the authMiddleware on routes instead
 * @param checkAuthenticated if set to true, will redirect to the /auth/login if the user is not authenticated.
 * If false, will redirect to the index if the user *is* authenticated.
 * @param checkEmailVerified if set to true, will redirect to the /verify-email if the email is not verified.
 * If false, will redirect to the index if the email *has* been verified.
 * @returns
 */
export function useAuthChecker(
  checkAuthenticated = true,
  checkEmailVerified = true,
): [User | undefined, boolean] {
  const navigate = useNavigate();

  const currentUserQuery = useQuery({
    queryKey: ["current_user"],
    queryFn: async () => {
      try {
        const res = await api.browser.userGetCurrent();

        identifyUser(res.data);
        if (!checkAuthenticated) {
          void navigate("/");
        } else if (checkEmailVerified && !res.data?.emailVerified) {
          void navigate("/verify-email");
        } else if (!checkEmailVerified && res.data?.emailVerified) {
          void navigate("/");
        }

        return res;
      } catch (_) {
        clearUser();
        if (checkAuthenticated) {
          void navigate("/auth/login");
        }
        return null;
      }
    },
    // Requery every 10 seconds for the current user
    refetchInterval: 10000,
    retry: false,
  });

  return [currentUserQuery.data?.data || undefined, currentUserQuery.isPending];
}

export function useFeatureFlags(tenantId: string | undefined) {
  const { data: featureFlags, isLoading } = useQuery({
    ...queries.tenantFeatureFlag.list(tenantId),
    enabled: !!tenantId,
    retry: false,
  });

  const hasFeatureFlag = useCallback(
    (flag: string) => {
      if (!tenantId) {
        return false;
      }

      if (isLoading) {
        return false;
      }

      if (!featureFlags) {
        return false;
      }

      return featureFlags.some((f) => f.id === flag);
    },
    [featureFlags, isLoading, tenantId],
  );

  return { hasFeatureFlag, isLoading };
}

type Tabs = {
  [key: string]:
    | string
    | {
        value: string;
        label: string;
        href?: string;
        endDecorator?: ReactNode;
        externalLink?: string;
        icon?: JSX.Element;
        render?: (active: boolean) => JSX.Element;
      };
};

export type ReturnTab<T = string> = {
  label: string;
  value: T;
  current: boolean;
  externalLink?: string;
  icon?: JSX.Element;
  render?: (active: boolean) => JSX.Element;
  href?: string;
  endDecorator?: ReactNode;
};

export function useTabs<T extends Tabs>({
  tabs,
  defaultTab,
  useSearchParam = true,
  searchParamName = "tab",
  replaceHistory = true,
}: {
  tabs: T;
  defaultTab: keyof typeof tabs;
  /**
   * Whether to sync the tabs with a search param in the url
   */
  useSearchParam?: boolean;
  /**
   * The name of the search param to use in the url
   */
  searchParamName?: string;
  /**
   * Whether to replace the current history entry when switching tabs and using a search param.
   * Defaults to true to prevent switching tabs from adding new history entries.
   */
  replaceHistory?: boolean;
}): {
  tabs: ReturnTab<keyof T>[];
  activeTab: keyof T;
  setActiveTab: (tab: keyof T) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  let initialTab = useSearchParam
    ? (searchParams.get(searchParamName) ?? defaultTab)
    : defaultTab;

  if (typeof initialTab !== "string" || !(initialTab in tabs)) {
    initialTab = defaultTab;
  }

  const [activeTab, setActiveTab] = useState<keyof T>(initialTab);
  const navigate = useNavigate();

  const changeTab = useCallback(
    (newTab: keyof T) => {
      const tabInfo = tabs[newTab];
      const externalLink =
        typeof tabInfo === "string" ? undefined : tabInfo.externalLink;

      if (externalLink) {
        void navigate(externalLink);
        return;
      }

      if (useSearchParam && typeof newTab === "string") {
        setSearchParams(
          (prev) => {
            prev.set(searchParamName, newTab);
            return prev;
          },
          {
            replace: replaceHistory,
          },
        );
      }

      setActiveTab(newTab);
    },
    [
      tabs,
      useSearchParam,
      searchParamName,
      setSearchParams,
      replaceHistory,
      navigate,
    ],
  );

  useEffect(() => {
    if (useSearchParam) {
      const urlTab = searchParams.get(searchParamName);
      if (urlTab && urlTab !== activeTab) {
        setActiveTab(urlTab);
      } else if (!urlTab && activeTab !== defaultTab) {
        setActiveTab(defaultTab);
      }
    }
  }, [useSearchParam, searchParamName, searchParams, activeTab, defaultTab]);

  const tabsArray = Object.entries(tabs).map(([key, value]) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(searchParamName, key);
    const href = `${location.pathname}?${newSearchParams.toString()}`;

    if (typeof value === "string") {
      return {
        label: value,
        value: key,
        current: key === activeTab,
        externalLink: undefined,
        icon: undefined,
        render: undefined,
        href,
        endDecorator: undefined,
      };
    }

    return {
      label: value.label,
      value: key,
      current: key === activeTab,
      externalLink: value.externalLink,
      icon: value.icon,
      render: value.render,
      href: value.href ?? href,
      endDecorator: value.endDecorator,
    };
  });

  return { tabs: tabsArray, activeTab: activeTab, setActiveTab: changeTab };
}

export function useTextFilter<T>({
  items,
  useFuzzySearch = false,
  options,
  onChange,
  searchOptions,
}: {
  items: T[];
  useFuzzySearch?: boolean;
  options?: IFuseOptions<T> & { value?: string; defaultValue?: string };
  onChange?: (value: string) => void;
  searchOptions?: FuseSearchOptions;
}) {
  if (!Array.isArray(items)) {
    throw new Error("useTextFilter only accepts arrays");
  }

  const [filter, setFilter] = useState(
    options?.value ?? options?.defaultValue ?? "",
  );

  useEffect(() => {
    if (options?.value !== undefined) {
      setFilter(options.value);
    }
  }, [options?.value]);

  const [filteredItems, fuseRes] = useMemo(() => {
    const fuse = new Fuse(items, {
      ...(!useFuzzySearch && { threshold: 0, ignoreLocation: true }),
      ...options,
    });

    if (filter) {
      const res = fuse.search(filter, searchOptions);
      return [res.map((result) => result.item), res];
    }

    const itemsWithLimit = searchOptions?.limit
      ? items.slice(0, searchOptions.limit)
      : items;
    return [itemsWithLimit, []];
  }, [items, filter, options, searchOptions, useFuzzySearch]);

  const inputProps = {
    value: filter,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(e.target.value);
      onChange?.(e.target.value);
    },
  };

  return { filteredItems, inputProps, fuseRes, setFilter };
}

export function useForwardedRef<T>(ref: React.ForwardedRef<T>) {
  const innerRef = useRef<T>(null);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === "function") {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });

  return innerRef;
}

export function useScrollFaders<T extends HTMLElement = HTMLDivElement>() {
  const containerRef = useRef<T>(null);
  const [scrollIsAtLeft, setScrollIsAtLeft] = useState(false);
  const [scrollIsAtRight, setScrollIsAtRight] = useState(false);

  const handleScroll = useCallback(
    (target: HTMLElement) => {
      const { scrollLeft, scrollWidth, clientWidth } = target;

      const isAtLeft = scrollLeft === 0;
      const isAtRight = Math.abs(scrollLeft + clientWidth - scrollWidth) < 1; // give a small margin of error;

      if (isAtLeft && !scrollIsAtLeft) {
        setScrollIsAtLeft(true);
      } else if (!isAtLeft && scrollIsAtLeft) {
        setScrollIsAtLeft(false);
      }

      if (isAtRight && !scrollIsAtRight) {
        setScrollIsAtRight(true);
      } else if (!isAtRight && scrollIsAtRight) {
        setScrollIsAtRight(false);
      }
    },
    [scrollIsAtLeft, scrollIsAtRight],
  );

  useEffect(() => {
    const elem = containerRef.current;

    const handleResize = () => {
      if (containerRef.current) {
        handleScroll(containerRef.current);
      }
    };

    window.addEventListener("resize", handleResize);
    elem?.addEventListener("scroll", (ev) =>
      handleScroll(ev.currentTarget as HTMLElement),
    );

    return () => {
      window.removeEventListener("resize", handleResize);
      elem?.removeEventListener("scroll", (ev) =>
        handleScroll(ev.currentTarget as HTMLElement),
      );
    };
  }, [handleScroll]);

  useEffect(() => {
    if (containerRef.current) {
      handleScroll(containerRef.current);
    }
  });

  return {
    containerRef,
    scrollIsAtLeft,
    scrollIsAtRight,
  };
}
